import type { CompletionSource } from "@codemirror/autocomplete"
import type { ColorScheme } from "@motley/design"
import type { Diagnostic } from "@codemirror/lint"
import type { JSONSchema7 } from "json-schema"
import type { EditorView } from "@codemirror/view"
import { hoverTooltip } from "@codemirror/view"
import { ScriptLanguage } from "./model"
import type { JsonValidator } from "./model"

/** @knocklabs/codemirror-json-schema's mode key for plain JSON, which it doesn't export. */
const JSON_MODE = "json4"

const jsonSchemaExtensions = async (
  jsonSchema: JSONSchema7,
  validate: JsonValidator | undefined,
  complete: CompletionSource | undefined,
) => {
  const { getJsonPointers, handleRefresh, jsonSchemaHover, stateExtensions } =
    await import("@knocklabs/codemirror-json-schema")
  const { json, jsonLanguage } = await import("@codemirror/lang-json")
  const { linter, lintGutter } = await import("@codemirror/lint")

  // Completion parses partial documents on every keystroke and logs the expected failures.
  const { disableErrorLogging } = await import("best-effort-json-parser")
  disableErrorLogging()

  const schemaLinter =
    (validateField: JsonValidator) =>
    (view: EditorView): Diagnostic[] => {
      const text = view.state.doc.toString()
      if (!text.length) {
        return []
      }

      let data: unknown
      try {
        data = JSON.parse(text)
      } catch {
        // Syntax errors belong to the JSON language, not to the schema.
        return []
      }

      const issues = validateField(data)
      if (!issues.length) {
        return []
      }

      const pointers = getJsonPointers(view.state, JSON_MODE)

      // The pointer map only holds property names and objects, so an issue on a missing
      // property resolves against its nearest addressable ancestor.
      const locate = (pointer: string): Pick<Diagnostic, "from" | "to"> => {
        const position = pointers.get(pointer)
        if (position) {
          return "valueFrom" in position
            ? { from: position.valueFrom, to: position.valueTo }
            : { from: position.keyFrom, to: position.keyTo }
        }

        return pointer
          ? locate(pointer.slice(0, pointer.lastIndexOf("/")))
          : { from: 0, to: 0 }
      }

      return issues.map(({ pointer, message }) => ({
        ...locate(pointer),
        message,
        severity: "error",
      }))
    }

  return [
    json(),
    ...(validate
      ? [
          lintGutter(),
          linter(schemaLinter(validate), { needsRefresh: handleRefresh }),
        ]
      : []),
    ...(complete ? [jsonLanguage.data.of({ autocomplete: complete })] : []),
    hoverTooltip(jsonSchemaHover()),
    stateExtensions(jsonSchema),
  ]
}

const languageExtension = async (
  language: ScriptLanguage | "json",
  jsonSchema?: JSONSchema7,
  validate?: JsonValidator,
  complete?: CompletionSource,
) => {
  switch (language) {
    case ScriptLanguage.JavaScript:
      return [(await import("@codemirror/lang-javascript")).javascript()]
    case ScriptLanguage.Python:
      return [(await import("@codemirror/lang-python")).python()]
    case "json":
      return jsonSchema
        ? await jsonSchemaExtensions(jsonSchema, validate, complete)
        : [(await import("@codemirror/lang-json")).json()]
  }
}

export const getExtensions = async (
  language: ScriptLanguage | "json",
  jsonSchema?: JSONSchema7,
  validate?: JsonValidator,
  complete?: CompletionSource,
) => await languageExtension(language, jsonSchema, validate, complete)

export const getTheme = async (colorScheme: ColorScheme | undefined) =>
  (await import("@uiw/codemirror-theme-github"))[
    colorScheme === "dark" ? "githubDark" : "githubLight"
  ]
